import { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import TablePagination from "@mui/material/TablePagination";
import { useNavigate } from "react-router-dom";
import { useProxyList } from "./useProxyList";
import { useProxyListActions } from "./useProxyListActions";
import type { VerificationSelection, ProxyGroup } from "./useProxyListActions";
import { ProxyListFilters } from "./ProxyListFilters";
import { ProxyDataGrid } from "./ProxyDataGrid";
import { VerifyProxiesDialog } from "./VerifyProxiesDialog";
import { DeactivateProxiesDialog } from "./DeactivateProxiesDialog";
import { SetProxyOriginDialog } from "./SetProxyOriginDialog";
import { ProxyVerificationProgress } from "./ProxyVerificationProgress";
import { IfPermission } from "../../auth/IfPermission";
import type { JSX } from "react";

export function ProxyList() {
  let proxyList: JSX.Element;
  const navigate = useNavigate();
  const {
    state,
    fetching,
    page,
    pageSize,
    filterStatus,
    filterGroup,
    setFilterStatus,
    setFilterGroup,
    setPage,
    setPageSize,
    refetch,
  } = useProxyList();
  const {
    verification,
    deactivation,
    setOrigin,
    startVerification,
    deactivateProxies,
    setProxyOrigin,
    resetVerification,
    resetDeactivation,
    resetSetOrigin,
  } = useProxyListActions(refetch);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [setOriginDialogOpen, setSetOriginDialogOpen] = useState(false);
  const handleVerifyConfirm = (selection: VerificationSelection) => {
    const proxyIds = selection === "selected" ? Array.from(selectedIds) : [];
    startVerification(selection, proxyIds);
  };
  const handleDeactivateConfirm = () => {
    deactivateProxies(Array.from(selectedIds));
    setDeactivateDialogOpen(false);
    setSelectedIds(new Set());
  };
  const handleSetOriginConfirm = (group: ProxyGroup) => {
    setProxyOrigin(Array.from(selectedIds), group);
    setSetOriginDialogOpen(false);
    setSelectedIds(new Set());
  };
  const showVerificationProgress = verification.kind !== "idle";
  // MUI считает страницы с нуля, экран — с единицы.
  const zeroBasedPage = page - 1;

  if (state.kind === "loading") {
    proxyList = (
      <Box p={3}>
        <Typography variant="h5" mb={2}>Прокси</Typography>
        <Box mb={2}>
          <ProxyListFilters
            status={filterStatus}
            group={filterGroup}
            onStatusChange={setFilterStatus}
            onGroupChange={setFilterGroup}
          />
        </Box>
        <Box display="flex" justifyContent="center" p={6}>
          <CircularProgress />
        </Box>
      </Box>
    );
  } else {
    if (state.kind === "error") {
      proxyList = (
      <Box p={3}>
        <Typography variant="h5" mb={2}>Прокси</Typography>
        <Box mb={2}>
          <ProxyListFilters
            status={filterStatus}
            group={filterGroup}
            onStatusChange={setFilterStatus}
            onGroupChange={setFilterGroup}
          />
        </Box>
        <Alert
          severity="error"
          action={<Button size="small" onClick={refetch}>Повторить</Button>}
        >
          Ошибка загрузки: {state.message}
        </Alert>
      </Box>
    );
    } else {
      proxyList = (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Прокси</Typography>
        <Box display="flex" gap={1}>
          <IfPermission permission="proxies.import">
            <Button
              variant="contained"
              onClick={() => {
      void navigate("/proxies/create");
    }}
            >
              Импорт
            </Button>
          </IfPermission>
          <IfPermission permission="proxies.verify">
            <Button
              variant="outlined"
              onClick={() => setVerifyDialogOpen(true)}
              disabled={verification.kind === "starting" || verification.kind === "progress"}
            >
              Проверить прокси
            </Button>
          </IfPermission>
          <IfPermission permission="proxies.manage">
            <Button
              variant="outlined"
              color="error"
              disabled={selectedIds.size === 0}
              onClick={() => setDeactivateDialogOpen(true)}
            >
              Удалить ({selectedIds.size})
            </Button>
            <Button
              variant="text"
              color="inherit"
              disabled={selectedIds.size === 0}
              onClick={() => setSetOriginDialogOpen(true)}
            >
              Группа ({selectedIds.size})
            </Button>
          </IfPermission>
        </Box>
      </Box>

      <Box mb={2}>
        <ProxyListFilters
            status={filterStatus}
            group={filterGroup}
            onStatusChange={setFilterStatus}
            onGroupChange={setFilterGroup}
          />
      </Box>

      {fetching && <LinearProgress sx={{ mb: 1 }} />}

      {showVerificationProgress && (
        <ProxyVerificationProgress
          kind={verification.kind}
          summary={
            verification.kind === "progress" || verification.kind === "completed"
              ? verification.summary
              : null
          }
          errorMessage={verification.kind === "error" ? verification.message : null}
          onDismiss={resetVerification}
        />
      )}

      {state.kind === "empty" ? (
        <Box textAlign="center" py={4}>
          <Typography color="text.secondary" mb={2}>
            Нет прокси. Добавьте прокси через импорт.
          </Typography>
          <IfPermission permission="proxies.import">
            <Button variant="outlined" onClick={() => {
      void navigate("/proxies/create");
    }}>
              Импортировать прокси
            </Button>
          </IfPermission>
        </Box>
      ) : (
        <>
          <ProxyDataGrid
            items={state.items}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
          />
          <TablePagination
            component="div"
            count={state.totalCount}
            page={zeroBasedPage}
            onPageChange={(_event, newPage) => setPage(newPage + 1)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(event) => setPageSize(Number(event.target.value))}
            rowsPerPageOptions={[5, 10, 25, 50, 100, 250, 500, 1000]}
            labelRowsPerPage="На странице:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} из ${count}`
            }
          />
        </>
      )}

      <VerifyProxiesDialog
        open={verifyDialogOpen}
        selectedCount={selectedIds.size}
        onClose={() => setVerifyDialogOpen(false)}
        onConfirm={handleVerifyConfirm}
      />

      <DeactivateProxiesDialog
        open={deactivateDialogOpen}
        count={selectedIds.size}
        onClose={() => setDeactivateDialogOpen(false)}
        onConfirm={handleDeactivateConfirm}
      />

      <SetProxyOriginDialog
        open={setOriginDialogOpen}
        count={selectedIds.size}
        onClose={() => setSetOriginDialogOpen(false)}
        onConfirm={handleSetOriginConfirm}
      />

      <Snackbar
        open={deactivation.kind === "success"}
        autoHideDuration={4000}
        onClose={resetDeactivation}
        message={
          deactivation.kind === "success"
            ? `Удалено: ${deactivation.deactivatedCount}`
            : ""
        }
      />

      <Snackbar
        open={setOrigin.kind === "success"}
        autoHideDuration={4000}
        onClose={resetSetOrigin}
        message={
          setOrigin.kind === "success"
            ? `Назначена группа: ${setOrigin.updatedCount}`
            : ""
        }
      />
    </Box>
  );
    }
  }
  return proxyList;
}
